import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { displayDateTime } from '@/lib/dates'

interface SendingDomainLeaseStatusProps {
  domain: string
  status: 'pending' | 'verified' | 'degraded'
  verifiedAt: string | null
  degradedAt: string | null
  graceEndsAt: string | null
  platformIdentitySince: string | null
}

export function SendingDomainLeaseStatus({
  domain,
  status,
  verifiedAt,
  degradedAt,
  graceEndsAt,
  platformIdentitySince,
}: SendingDomainLeaseStatusProps) {
  const verified = status === 'verified'
  const degraded = status === 'degraded'
  const badgeLabel = verified ? 'Verified' : degraded ? 'Degraded' : 'Pending verification'

  return (
    <>
      <div className="flex items-center gap-2 text-body-sm">
        <span className="font-medium text-foreground">{domain}</span>
        <Badge variant={verified ? 'success' : 'warning'}>{badgeLabel}</Badge>
      </div>
      {degraded && (
        <Alert variant="warning">
          <p className="font-medium">Sending Domain DNS needs attention</p>
          <p className="mt-1">
            {platformIdentitySince
              ? 'Email is using the platform identity until DNS verification returns.'
              : `Email keeps this Sending Domain through ${formatDomainTime(graceEndsAt)}.`}
          </p>
        </Alert>
      )}
      {(verifiedAt || degradedAt) && (
        <dl className="grid gap-1 text-body-sm text-muted-foreground sm:grid-cols-2">
          <DomainTimestamp label="Verified" value={verifiedAt} />
          <DomainTimestamp label="Degraded" value={degradedAt} />
          <DomainTimestamp label="Platform identity since" value={platformIdentitySince} />
        </dl>
      )}
    </>
  )
}

function DomainTimestamp({ label, value }: { label: string; value: string | null }) {
  if (!value) return null

  return (
    <div>
      <dt className="inline font-medium text-foreground">{label} </dt>
      <dd className="inline">{formatDomainTime(value)}</dd>
    </div>
  )
}

function formatDomainTime(value: string | null) {
  if (!value) return 'the end of the grace window'
  return displayDateTime(value)
}
